import React, { useEffect } from 'react'
import { isEmpty, isNil, groupBy } from 'ramda'
import {
  Select as BaseSelect,
  SelectGroup,
  SelectGroupLabel,
  SelectLabel,
  SelectPopover,
  SelectProvider,
} from '@ariakit/react'

import { Icon, IconSize } from '@sidecar-components/atoms/Icon'
import { SelectOption } from '@sidecar-components/atoms/SelectOption'
import { InputHelperText } from '@sidecar-components/atoms/InputHelperText'
import {
  containerStyle,
  labelStyle,
  selectStyle,
  iconStyle,
  valueStyle,
  popoverStyle,
  groupStyle,
  groupTitleStyle,
  sublabelStyle,
} from './styles'
import { SelectProps } from './Select.types'

export const Select = ({
  autoFocus,
  className = '',
  condensed,
  disabled,
  error,
  helperText,
  id,
  isMultiSelect = false,
  label,
  leftMedia,
  name,
  onBlur,
  onBottomDetection,
  onChange,
  onFocus,
  optionClass = '',
  options = [],
  placeholder = 'Select a value',
  showSelectedSublabel,
  testId,
  value,
  wrapperClass = '',
}: SelectProps) => {
  // Don't set the default value here, we'll need to do it in a useEffect
  // so that it can be updated later on as well.
  const [selectedValue, setSelectedValue] = React.useState<string | string[]>(
    '',
  )

  // Ref for the popover element to attach scroll listener
  const popoverRef = React.useRef<HTMLDivElement>(null)

  // Update the selectedValue state when the value prop changes or else we
  // can't change the value of this component once it's set.
  useEffect(() => {
    setSelectedValue(value)
  }, [value])

  // Add scroll listener for bottom detection
  useEffect(() => {
    const popoverElement = popoverRef.current
    if (!popoverElement || !onBottomDetection) return

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = popoverElement
      // Trigger when user scrolls to within 10px of the bottom
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10

      if (isNearBottom) {
        onBottomDetection()
      }
    }

    popoverElement.addEventListener('scroll', handleScroll)
    return () => popoverElement.removeEventListener('scroll', handleScroll)
  }, [onBottomDetection])

  // Handler for when the selectedValue state is changed, this is
  // where we should call any onChange callbacks.
  const handleSelectValue = selectedValue => {
    setSelectedValue(selectedValue)
    onChange && onChange(selectedValue)
  }

  const selectedItem = React.useMemo(() => {
    return options.find(({ value }) => selectedValue === value)
  }, [selectedValue, options])

  // isMultiSelect needs to handle multiple selected items
  const multiSelectedItems = React.useMemo(() => {
    return isMultiSelect
      ? options.filter(option => selectedValue?.includes(option.value))
      : []
  }, [selectedValue, options, isMultiSelect])

  // Group options by the group value
  const groupedOptions = groupBy(option => option.group || '', options)

  // Allowing the Select component to select multiple values by passing an array
  // to the defaultValue prop on the SelectProvider component.
  const noValue = isMultiSelect ? [] : ''
  const defaultValue = value || noValue

  return (
    <div className={wrapperClass}>
      <SelectProvider
        defaultValue={defaultValue}
        value={selectedValue}
        setValue={handleSelectValue}
      >
        <div className={`${containerStyle} ${className}`}>
          <SelectLabel
            className={labelStyle({
              condensed,
              disabled: disabled,
              hasError: !isNil(error),
              hasIcon: !isNil(leftMedia),
              // We adjust the size/position of the label if there's a value
              // in the field or not. This is so that the label doesn't overlap.
              hasValues: !isNil(selectedValue),
              showSelectedSublabel,
            })}
            data-testid="info_label"
          >
            {label}
          </SelectLabel>

          {showSelectedSublabel && selectedItem?.sublabel && (
            <span className={sublabelStyle({ hasIcon: !isNil(leftMedia) })}>
              {selectedItem.sublabel}
            </span>
          )}

          <BaseSelect
            // A Select trigger must never submit a parent form. Ariakit's
            // useCommand omits the type attribute pre-hydration (isNativeButton
            // resolves false while the ref is null), so the native <button>
            // defaults to type="submit" and submits any enclosing <form> on the
            // opening click. Forcing type="button" wins over Ariakit's default
            // even in the SSR/pre-hydration window.
            type="button"
            autoFocus={autoFocus}
            className={selectStyle({
              condensed: condensed,
              hasError: !isNil(error),
            })}
            disabled={disabled}
            name={name}
            id={id}
            aria-invalid={error ? true : null}
            aria-describedby={
              !!error || !!helperText
                ? `${error ? `${name}-error` : ''} ${
                    helperText ? `${name}-helper` : ''
                  }`
                : null
            }
            onBlur={onBlur}
            onFocus={onFocus}
            data-testid={testId}
          >
            {/* If the selected item has a left media, use that. 
          If not, use the select's left media. */}
            {!isNil(selectedItem?.leftMedia)
              ? selectedItem.leftMedia
              : leftMedia}

            <div
              className={valueStyle({
                condensed,
                disabled,
                hasValues: !isNil(selectedValue),
                showSelectedSublabel,
              })}
              data-testid="info_value"
            >
              {/* Show the placeholder if there is no selected item */}
              {!isMultiSelect &&
                (!selectedItem ? placeholder : selectedItem.label)}

              {isMultiSelect &&
                (isEmpty(multiSelectedItems)
                  ? placeholder
                  : multiSelectedItems.map(item => item.label).join(', '))}
            </div>

            <Icon
              name="chevronDown"
              size={condensed ? IconSize.SM : IconSize.MD}
              className={iconStyle(disabled)}
            />
          </BaseSelect>
        </div>

        <SelectPopover
          ref={popoverRef}
          gutter={4}
          sameWidth
          className={popoverStyle}
          // react-remove-scroll (used by Radix Dialog) attaches a non-passive
          // document-level wheel listener that calls preventDefault on events
          // whose target is outside the allowlisted Content tree. Our portaled
          // popover is a sibling of (not inside) the Content, so wheel events
          // bubble up to document and get killed. Stop propagation so the lock
          // never sees them.
          onWheel={e => e.stopPropagation()}
          onTouchMove={e => e.stopPropagation()}
        >
          {Object.entries(groupedOptions).map(([groupName, groupOptions]) =>
            groupName ? (
              // If there is a group, wrap its items in a group and group label
              <SelectGroup key={groupName} className={groupStyle}>
                <SelectGroupLabel className={groupTitleStyle}>
                  {groupName}
                </SelectGroupLabel>
                {groupOptions.map(option => (
                  <SelectOption
                    testId={`${testId}_${option.value}`}
                    key={option.value}
                    option={option}
                    optionClass={optionClass}
                    leftMedia={leftMedia}
                    isMultiSelect={isMultiSelect}
                  />
                ))}
              </SelectGroup>
            ) : (
              // Leave ungrouped items unwrapped
              groupOptions.map(option => (
                <SelectOption
                  testId={`${testId}_${option.value}`}
                  key={option.value}
                  option={option}
                  optionClass={optionClass}
                  leftMedia={leftMedia}
                  isMultiSelect={isMultiSelect}
                />
              ))
            ),
          )}
        </SelectPopover>

        {/* The error and helper text will be separate from the input just so
          we keep our sanity when trying to position everything. */}
        <InputHelperText name={name} error={error} helperText={helperText} />
      </SelectProvider>
    </div>
  )
}
